import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { ErrorMessage } from 'types/shared/error-message.type';
import { clearState, loadState } from 'utils/storage';
import { useOrderFeedState } from 'views/order-feed/context/order-feed-context';
import { useOrderFeedSettingsState } from 'views/order-feed/context/order-feed-settings-context';
import { postOrder } from 'views/order-feed/features/my-supply-plan/api/orders-api';
import { useMySupplyPlanState } from 'views/order-feed/features/my-supply-plan/context/my-supply-plan-context';
import CreateOrderForm from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/components/order/create-order-form';
import { OrderListItem } from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/types/order-list-item.type';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import { CreateOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/create-order-form-values.type';
import { CreateOrderInput } from 'views/order-feed/features/my-supply-plan/types/create-order-input.type';
import { SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';
import { MySupplyPlanParams } from 'views/order-feed/features/my-supply-plan/types/my-supply-plan-params.type';
import {
  buildCreateOrderInput,
  getDefaultCreateOrderDispatchDate,
  getDefaultTransportDetails,
} from 'views/order-feed/features/my-supply-plan/utils/create-order-utils';
import { getOrderDateParts } from 'views/order-feed/features/my-supply-plan/utils/order-utils';

type Props = {
  orders: OrderListItem[] | undefined;
};

const PAGE_NAME_ID = 'orderFeed.listOrderSupplyPlan';
const CREATE_ORDER_DATA_KEY = 'createOrderData';

const CreateOrder: React.FC<Props> = ({ orders }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const params = useParams<MySupplyPlanParams>();
  const { account } = useAccountState();
  const {
    orderSettingsQuery: { data: orderSettings },
  } = useOrderFeedSettingsState();
  const {
    dispatchDatesQuery: { data: dispatchSchedule },
  } = useOrderFeedState();
  const {
    repeatOrder: { data: repeatOrderData },
  } = useMySupplyPlanState();
  const snackbarDispatch = useSnackbarDispatch();
  const { createOrderFeedEvent } = useGoogleTagManager();

  const defaultDispatchDate = useMemo(() => getDefaultCreateOrderDispatchDate(dispatchSchedule, orders), [dispatchSchedule, orders]);
  const defaultTransportDetails = useMemo(
    () => getDefaultTransportDetails(account.company?.addressAccess, params.globalSiteId),
    [account.company?.addressAccess, params.globalSiteId]
  );

  const defaultValues = useMemo<CreateOrderFormValues>(() => {
    const createOrderData = loadState({ key: CREATE_ORDER_DATA_KEY });
    if (createOrderData) return createOrderData;

    return {
      customerOrderReference: '',
      transportMethod: defaultTransportDetails.transportMethod,
      addressId: defaultTransportDetails.addressId,
      requestedDispatchDate: defaultDispatchDate,
      siteErpId: params.siteErpId,
      orderLines: [{ itemNumber: '', orderQuantity: 0 }],
      orderComment: '',
    };
  }, [defaultDispatchDate, defaultTransportDetails, params.siteErpId]);

  const form = useForm<CreateOrderFormValues>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });
  const { reset } = form;

  const handleReset = useCallback(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onCreateSuccess = (orderNumber: string) => {
    snackbarDispatch({
      type: SnackbarActionTypes.SUCCESS_SNACKBAR,
      payload: { msg: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderList.createOrder.orderCreated` }) },
    });
    clearState({ key: CREATE_ORDER_DATA_KEY });
    handleReset();
    queryClient.invalidateQueries({ queryKey: ['getOrders', account.company!.customerErpId, params.siteErpId] });

    createOrderFeedEvent('create', {
      orderNumber,
      supplyPlanType: SupplyPlanType.OFD1,
    });
  };

  const createOrder = usePost<string, { data: CreateOrderInput }, AxiosError<ErrorMessage[]>>(
    (request) => postOrder(account.company!.customerErpId, request.data).then((response) => response.orderNumber),
    onCreateSuccess,
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: {
          msg: intl.formatMessage({ id: 'snackbar.errors.default' }),
          error,
          errorMsg: error.response?.data,
        },
      });
    }
  );

  const handleCreateOrder = (data: CreateOrderFormValues) => {
    const { requestedDispatchDate, plantId, vessel } = getOrderDateParts(data.requestedDispatchDate);
    createOrder.mutate({
      data: buildCreateOrderInput({
        ...data,
        requestedDispatchDate,
        plantId,
        vessel,
      }),
    });
  };

  useEffect(() => {
    if (repeatOrderData) reset(repeatOrderData);
  }, [repeatOrderData, reset]);

  useEffect(() => {
    handleReset();
  }, [defaultDispatchDate, params, handleReset]);

  if (!params.globalSiteId || orderSettings?.isUnderMaintenance || !account.company?.features.hasOrderFeed) return null;

  return (
    <FormProvider {...form}>
      <CreateOrderForm createOrderIsLoading={createOrder.isPending} handleCreateOrder={handleCreateOrder} />
    </FormProvider>
  );
};

export default CreateOrder;
